// components/common/FieldTypeConfig.jsx
import { useState, useEffect, useContext } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';

import { db, timestamp } from '../../firebase/config';
import { client } from '../../hooks/Client';
import useSettings from '../../hooks/Settings';
import { useFirestoreGeneralOrderBy } from '../../firebase/useFirestore';
import usePersonaTerms from '../../hooks/usePersonaTerms';

import Tooltip from '../common/Tooltip.jsx';
import ToggleButton from './ToggleButton.jsx';
import DeleteModal from './DeleteModal.jsx';
import softDeleteDoc from '../../helpers/softDeleteDoc.js';
import { Auth } from '../../StateManagment/Auth';

import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';

import deleteIcon from '../../images/icons/delete-icon.png';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import otherIcon from '../../images/icons/other-icon.png';
import { isFlagOff } from '../../helpers/isFlagOn';

/**
 * Shared field-type config / preview component.
 *
 * Props:
 * - field: Firestore doc object for the field (must contain at least: ID, docid, Type, ReachStart, ReachEnd, etc.)
 * - fieldCollection: the Firestore collection name where the field lives (e.g. 'EventFields', 'CustomEventFields')
 * - personas?: array (for 'participants' type)
 * - outputs?: array (for 'outputs' type)
 * - targetgroups?: array (for 'targetgroups' type)
 */
const ActivityFieldTypeConfig = ({ field, fieldCollection = 'EventFields', personas, outputs, targetgroups }) => {

  const { SecundairyColor: secundairyColor, personasOn: persona } = useSettings();
  const personaTerms = usePersonaTerms();

  // Multiple options
  const multipleOptions = useFirestoreGeneralOrderBy(
    'MultipleQuestionOptions',
    'Field',
    field?.ID || '',
    'Position',
    'asc'
  );

  const [optionsDrag, setOptionsDrag] = useState([]);
  const [auth] = useContext(Auth);
  const [deleteModal, setDeleteModal] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);

  useEffect(() => {
    setOptionsDrag(multipleOptions || []);
  }, [multipleOptions]);

  // --- Helpers to update the field doc itself ---

  const updateField = (patch) => {
    if (!field?.docid) return;

    db.collection(fieldCollection)
      .doc(field.docid)
      .update(patch)
      .catch((err) => console.error('Error updating field:', err));
  };

  // Scale handlers
  const reachStartHandler = (e) => {
    const value = e.target.value;
    updateField({ ReachStart: value });
  };

  const reachEndHandler = (e) => {
    const value = e.target.value;
    updateField({ ReachEnd: value });
  };

  const reachStartLabelHandler = (e) => {
    const value = e.target.value;
    updateField({ ReachStartLabel: value });
  };

  const reachEndLabelHandler = (e) => {
    const value = e.target.value;
    updateField({ ReachEndLabel: value });
  };

  // --- Multiple options CRUD + DnD ---

  const saveMultiple = async () => {
    if (!field?.ID) return;

    await db.collection('MultipleQuestionOptions').doc().set({
      Option: '',
      Question: '',
      Field: field.ID,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Position: (multipleOptions?.length || 0) + 1,
      ID: uuid(),
      Color: secundairyColor,
    });
  };

  const addOtherHandler = async () => {
    if (!field?.ID) return;

    await db.collection('MultipleQuestionOptions').doc().set({
      Option: 'other',
      Question: field.Title || '',
      Field: field.ID,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Position: (multipleOptions?.length || 0) + 1,
      ID: uuid(),
      Color: secundairyColor,
    });
  };

  const editMultipleHandler = (e) => {
    const value = e.target.value;
    const optionDocId = e.target.dataset.docid;

    db.collection('MultipleQuestionOptions')
      .doc(optionDocId)
      .update({ Option: value })
      .catch((err) => console.error('Error updating option:', err));
  };

  // Eerst bevestigen: één klik verwijderde voorheen een antwoordoptie
  // meteen, zonder vraag en zonder weg terug.
  const askDeleteMultiple = (item) => {
    setOptionToDelete(item);
    setDeleteModal(true);
  };

  const deleteMultiple = async () => {
    if (!optionToDelete?.docid) return;
    await softDeleteDoc({
      collection: 'MultipleQuestionOptions',
      docid: optionToDelete.docid,
      authId: auth?.ID,
    });
  };

  const handleOptionsDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(optionsDrag);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setOptionsDrag(items);

    items.forEach((opt, idx) => {
      db.collection('MultipleQuestionOptions')
        .doc(opt.docid)
        .update({ Position: idx + 1 })
        .catch((err) => console.error('Error updating option position:', err));
    });
  };

  if (!field) return null;

  // --- Renderer by type (this is your original switch, now shared) ---
  // De bevestiging moet blijven staan welk veldtype er ook getoond wordt, dus
  // het veldtype-specifieke deel gaat in een eigen functie.
  const renderFieldType = () => {
    switch (field.Type) {
    case 'date':
      return (
        <div className='event-builder-type-item-container event-builder-type-item-date-container'>
          <input type="date" />
        </div>
      );

    case 'participants':
      return (
        <div className='event-builder-type-item-container event-builder-type-item-multiple-container'>
          {personas && personas.map(persona => (
            <div key={persona.ID} id='add-event-persona-item-container'>
              <input type="checkbox" />
              <label>{persona.Name}</label>
            </div>
          ))}
        </div>
      );

    case 'outputs':
      return (
        <div className='event-builder-type-item-container'>
          {outputs && outputs.map(output => (
            <div key={output.ID} id='add-event-output-item-container'>
              <div className='event-output-row'>
                <p>{output.Title}</p>
                <input className='event-output-input' type="number" placeholder={0} />
              </div>
              {persona === 'flex' && (
                <div className='reasearch-builder-option-container'>
                  <div className='research-builder-options-title-container'>
                    <p><b>Koppel aan {personaTerms.pluralLower}</b></p>
                  </div>
                  <ToggleButton
                    docid={output.docid}
                    id="LinkParticipantsToOutputs"
                    value={output.LinkParticipantsToOutputs || 'true'}
                    collection="Outputs"
                  />
                </div>
              )}
              {persona === 'flex' && !isFlagOff(output.LinkParticipantsToOutputs) && (
                <div className='event-output-personas-container'>
                  {personas && personas.map(personaItem => (
                    <div key={`${output.ID}-${personaItem.ID}`} className='event-output-persona-option'>
                      <input type="checkbox" />
                      <label>{personaItem.Name}</label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      );

    case 'targetgroups':
      return (
        <div className='event-builder-type-item-container event-builder-type-item-multiple-container'>
          {targetgroups && targetgroups.map(targetgroup => (
            <div key={targetgroup.ID} id='add-event-targetgroup-item-container'>
              <input type="checkbox" />
              <label>{targetgroup.Organisation}</label>
            </div>
          ))}
        </div>
      );

    case 'open':
      return (
        <div className='event-builder-type-item-container'>
          <textarea placeholder="Schrijf hier je antwoord"></textarea>
        </div>
      );

    case 'scale':
      return (
        <div className='event-builder-type-item-container'>
          <div className='edit-scale-container'>
            <div className='select-scale-container'>
              {/* ?? en niet ||: een onderwaarde 0 is een keuze.
                  AI-CONTEXT: See docs/pitfalls.md#state-or-fallback-erases-zero */}
              <select onChange={reachStartHandler} defaultValue={field.ReachStart ?? ''}>
                <option value="">-- Selecteer onderwaarde ---</option>
                <option value={0}>0</option>
                <option value={1}>1</option>
              </select>
              <input
                type="text"
                placeholder='Voeg label toe (optioneel)'
                defaultValue={field.ReachStartLabel || ''}
                onChange={reachStartLabelHandler}
              />
            </div>
            <p id='scale-reach-symbol'>t/m</p>
            <div className='select-scale-container'>
              <select onChange={reachEndHandler} defaultValue={field.ReachEnd ?? ''}>
                <option value="">-- Selecteer bovenwaarde --</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
                <option value={6}>6</option>
                <option value={7}>7</option>
                <option value={8}>8</option>
                <option value={9}>9</option>
                <option value={10}>10</option>
              </select>
              <input
                type="text"
                placeholder='Voeg label toe (optioneel)'
                defaultValue={field.ReachEndLabel || ''}
                onChange={reachEndLabelHandler}
              />
            </div>
          </div>
        </div>
      );

    case 'number':
      return (
        <div className='event-builder-type-item-container'>
          <input type="number" placeholder={0} />
        </div>
      );

    case 'multiple-one':
    case 'multiple-multiple':
      return (
        <div className='event-builder-type-item-container event-builder-type-item-multiple-container'>
          {/* Actions */}
          <div className='questionnaire-suggestion-container'>
            <Tooltip content='Antwoord toevoegen' top='-60px' width={'30px'}>
              <AddCircleOutlineOutlinedIcon
                className='table-delete-icon'
                onClick={saveMultiple}
              />
            </Tooltip>
            <Tooltip content="Anders, namelijk.. toevoegen" top='-80px' width={'30px'}>
              <img
                src={otherIcon}
                alt=""
                onClick={addOtherHandler}
              />
            </Tooltip>
          </div>

          {/* Options with react-beautiful-dnd */}
          <DragDropContext onDragEnd={handleOptionsDragEnd}>
            <Droppable droppableId={`options-${field.ID}`} direction="vertical">
              {(provided) => (
                <div id='options-container' ref={provided.innerRef} {...provided.droppableProps}>
                  {optionsDrag && optionsDrag.map((item, idx) => (
                    <Draggable key={item.ID} draggableId={item.ID} index={idx}>
                      {(provided) => (
                        <div
                          className='multiple-option-row'
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          {/* Drag handle */}
                          <span className='drag-handle' {...provided.dragHandleProps}>
                            <DragIndicatorOutlinedIcon />
                          </span>

                          {item.Option === 'other' ? (
                            <>
                              <CheckBoxOutlineBlankOutlinedIcon />
                              <p id='multiple-question-other'>Anders, namelijk...</p>
                              <img
                                className='muitple-delete-icon'
                                onClick={() => askDeleteMultiple(item)}
                                src={deleteIcon}
                                alt=""
                                data-index={idx}
                              />
                            </>
                          ) : (
                            <>
                              <CheckBoxOutlineBlankOutlinedIcon />
                              <input
                                className='question-checkbox-input'
                                placeholder='Schrijf hier de antwoordoptie'
                                data-docid={item.docid}
                                defaultValue={item.Option}
                                onChange={editMultipleHandler}
                              />
                              <img
                                className='muitple-delete-icon'
                                onClick={() => askDeleteMultiple(item)}
                                src={deleteIcon}
                                alt=""
                                data-index={idx}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      );

    default:
      return <p>Geen type geselecteerd</p>;
    }
  };

  return (
    <>
      {renderFieldType()}
      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteMultiple}
        deleteName={optionToDelete?.Option || 'deze antwoordoptie'}
        softDelete
      />
    </>
  );
};

export default ActivityFieldTypeConfig;
